import { motion, useScroll, useTransform, useReducedMotion } from "framer-motion";
import { useEffect, useRef } from "react";
import { Reveal } from "@/components/Reveal";
import { useLanguage } from "@/context/LanguageContext";
import { media } from "@/config/products";

export default function MacroVideo() {
  const { t } = useLanguage();
  const ref = useRef(null);
  const videoRef = useRef(null);
  const reduce = useReducedMotion();
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start end", "end start"],
  });
  // Parallax muy sutil sobre el video (invisible pero inmersivo)
  const y = useTransform(scrollYProgress, [0, 1], ["-8%", "8%"]);
  const scale = useTransform(scrollYProgress, [0, 0.5, 1], [1.05, 1, 1.05]);
  // Gradual fade sincronizado con scroll:
  //   Entry (0 → 0.18): opacity 0 → 1  (aparece mientras el usuario baja)
  //   Reading window (0.18 → 0.55): opacity 1 (full presence)
  //   Slow fade-out (0.55 → 0.95): opacity 1 → 0.15 (se desvanece MIENTRAS
  //   el usuario aún está leyendo el heading, para que sienta que la escena
  //   se despide sola). Nunca llega a 0 completo — deja un aura tenue.
  const videoOpacity = useTransform(
    scrollYProgress,
    [0, 0.18, 0.55, 0.95],
    [0, 1, 1, 0.15]
  );

  // Pausa el video cuando sale del viewport (ahorra CPU/batería en móvil).
  // También respeta prefers-reduced-motion (no auto-play).
  useEffect(() => {
    const el = videoRef.current;
    if (!el) return;
    if (reduce) {
      el.pause();
      return;
    }
    if (!("IntersectionObserver" in window)) return;
    const io = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          el.play().catch(() => {});
        } else {
          el.pause();
        }
      },
      { threshold: 0.15 }
    );
    io.observe(el);
    // Pausar cuando la pestaña deja de ser visible
    const onVis = () => {
      if (document.visibilityState !== "visible") el.pause();
    };
    document.addEventListener("visibilitychange", onVis);
    return () => {
      io.disconnect();
      document.removeEventListener("visibilitychange", onVis);
    };
  }, [reduce]);

  return (
    <section
      ref={ref}
      data-testid="macro-section"
      className="relative flex min-h-[70vh] items-center justify-center overflow-hidden bg-[#050505] py-24"
    >
      <motion.div
        style={reduce ? undefined : { y, scale, opacity: videoOpacity }}
        className="absolute inset-0 h-full w-full"
      >
        <img
          src={media.macroPoster}
          alt=""
          aria-hidden="true"
          loading="lazy"
          decoding="async"
          className="absolute inset-0 h-full w-full object-cover opacity-70"
        />
        <video
          ref={videoRef}
          data-testid="macro-video"
          className="absolute inset-0 h-full w-full object-cover"
          src={media.macroVideo}
          poster={media.macroPoster}
          autoPlay={!reduce}
          muted
          loop
          playsInline
          preload="metadata"
        />
      </motion.div>
      <div className="absolute inset-0 bg-black/50" />
      <Reveal className="relative z-10 px-6 text-center" y={30}>
        <h2 className="font-serif-display text-3xl font-medium text-white sm:text-5xl">{t.macro.title}</h2>
        <p className="mt-4 font-body text-sm font-light text-white/70">{t.macro.support}</p>
      </Reveal>
    </section>
  );
}
